import { Link } from "react-router-dom";
import { CalendarDays, Clock, MessageSquare, FileSpreadsheet, Users, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/common";

const FEATURES = [
  { icon: Users, title: "Medewerkers", text: "Alle teamleden, rollen en uurlonen op één plek." },
  { icon: CalendarDays, title: "Weekplanning", text: "Diensten inplannen in een overzichtelijk weekrooster." },
  { icon: Clock, title: "In- & uitklokken", text: "Eén grote knop op de telefoon. Klaar." },
  { icon: MessageSquare, title: "Prikbord", text: "Belangrijke mededelingen en diensten ruilen." },
  { icon: FileSpreadsheet, title: "Urenexport", text: "Weekoverzicht als Excel/CSV voor je boekhouder." },
  { icon: ShieldCheck, title: "Privacy (AVG)", text: "Gegevens per bedrijf afgeschermd, rechten per rol." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="landing-page">
      <header className="mx-auto max-w-6xl px-5 h-16 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-1 sm:gap-2">
          <Button asChild variant="ghost" size="sm" className="px-2 sm:px-3" data-testid="landing-login-button"><Link to="/sign-in">Inloggen</Link></Button>
          <Button asChild size="sm" className="rounded-full px-3 sm:px-4" data-testid="landing-signup-button"><Link to="/sign-up">Gratis starten</Link></Button>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-5 pt-8 lg:pt-16 pb-16 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
        <div className="fade-up">
          <span className="inline-flex rounded-full bg-[#FFECE8] text-[#C2391B] px-3 py-1 text-xs font-semibold">
            Voor restaurants, sushi, snackbars & afhaal
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Rooster, uren en team. <span className="text-[#0D5C46]">Eindelijk simpel.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl">
            ShiftMate NL helpt kleine horecazaken met 5 tot 40 medewerkers om diensten te plannen, uren te registreren en
            het team te informeren. Zonder gedoe, gewoon op je telefoon.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full bg-[#FF5A36] hover:bg-[#E04826] h-12 px-7" data-testid="hero-start-button">
              <Link to="/sign-up">Start je bedrijf <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7" data-testid="hero-join-button">
              <Link to="/sign-up">Ik heb een uitnodigingscode</Link>
            </Button>
          </div>
        </div>
        <div className="relative fade-up fade-up-2">
          <img src="/images/hero-sushi.jpg" alt="Sushichef aan het werk" className="w-full h-[340px] lg:h-[460px] object-cover rounded-3xl" />
          <div className="absolute -bottom-5 left-5 right-5 sm:right-auto sm:w-72 rounded-2xl bg-white/95 backdrop-blur-lg p-4 shadow-xl border border-slate-200">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nu ingeklokt</p>
            <p className="font-display text-2xl font-bold text-slate-900">4 van 6 <span className="text-sm font-medium text-emerald-600">vandaag</span></p>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="mx-auto max-w-6xl px-5 py-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title} className={`rounded-2xl p-6 bg-[#FAFAF7] border border-slate-200 fade-up fade-up-${(i % 3) + 2}`}>
              <f.icon className="h-6 w-6 text-[#0D5C46]" />
              <h3 className="mt-4 font-semibold text-lg text-slate-900">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-1">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="mx-auto max-w-6xl px-5 py-8 text-sm text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
        <span>© {new Date().getFullYear()} ShiftMate NL</span>
        <span>Gegevens worden alleen gebruikt voor planning en urenregistratie. <Link to="/privacy" className="underline" data-testid="landing-privacy-link">Privacyverklaring</Link></span>
      </footer>
    </div>
  );
}
